import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Building2, UserPlus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/form-field';
import { apiRegisterTenant } from './api';
import { registerTenantSchema, type RegisterTenantInput } from './schemas';
import { useAuthStore } from './store';
import { extractErrorMessage } from '@/lib/api-client';

export function RegisterTenantPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Só existe um plano hoje, mas o parâmetro fica: se voltarem as faixas, a
  // landing volta a mandar `?plano=` sem mexer aqui.
  const [searchParams] = useSearchParams();
  const planCode = searchParams.get('plano') ?? undefined;

  const form = useForm<RegisterTenantInput>({
    resolver: zodResolver(registerTenantSchema),
    defaultValues: {
      companyName: '',
      cnpj: '',
      adminName: '',
      adminEmail: '',
      adminPassword: '',
      adminPhone: '',
      acceptTerms: false,
    },
  });

  const onSubmit = async (values: RegisterTenantInput) => {
    setSubmitError(null);
    try {
      const result = await apiRegisterTenant(values, planCode);
      setSession(result);
      navigate('/app', { replace: true });
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  const accept = form.watch('acceptTerms');

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/70 px-3 py-1 text-xs font-medium text-secondary-foreground">
          <Building2 className="size-3.5" />
          Cadastro de empresa
        </div>
        <h2 className="text-3xl font-semibold tracking-tight">Criar conta</h2>
        <p className="text-sm text-muted-foreground">
          O próximo passo é cadastrar seus veículos — é o que define o valor da sua
          mensalidade. Você vê o número exato antes de pagar.
        </p>
      </div>

      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Empresa
          </p>
          <FormField
            control={form.control}
            name="companyName"
            label="Razão social"
            placeholder="Transportadora Exemplo Ltda"
            autoFocus
          />
          <FormField
            control={form.control}
            name="cnpj"
            label="CNPJ"
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
            className="mt-3"
          />
        </div>

        <div className="space-y-1 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Administrador
          </p>
          <FormField
            control={form.control}
            name="adminName"
            label="Nome completo"
            placeholder="Seu nome"
            autoComplete="name"
          />
          <FormField
            control={form.control}
            name="adminEmail"
            label="E-mail"
            type="email"
            placeholder="voce@empresa.com.br"
            autoComplete="email"
            className="mt-3"
          />
          <FormField
            control={form.control}
            name="adminPhone"
            label="Telefone (opcional)"
            placeholder="+55 11 99999-9999"
            autoComplete="tel"
            className="mt-3"
          />
          <FormField
            control={form.control}
            name="adminPassword"
            label="Senha"
            type="password"
            placeholder="mínimo 10 caracteres"
            autoComplete="new-password"
            hint="Use pelo menos 10 caracteres com letras e números."
            className="mt-3"
          />
        </div>

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-card p-3 text-sm hover:bg-accent/30">
          <input
            type="checkbox"
            className="mt-0.5 size-4 cursor-pointer accent-[hsl(var(--primary))]"
            checked={accept}
            onChange={(e) => form.setValue('acceptTerms', e.target.checked, { shouldValidate: true })}
          />
          <span className="flex-1 leading-snug text-muted-foreground">
            Li e concordo com os{' '}
            <a href="#" className="font-medium text-primary hover:underline">
              Termos de Uso
            </a>{' '}
            e a{' '}
            <a href="#" className="font-medium text-primary hover:underline">
              Política de Privacidade
            </a>
            .
          </span>
        </label>
        {form.formState.errors.acceptTerms && (
          <p className="-mt-2 text-xs font-medium text-destructive">
            {form.formState.errors.acceptTerms.message}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={form.formState.isSubmitting}
        >
          <UserPlus className="size-4" />
          Criar conta
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Já tem conta?{' '}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Entrar
        </Link>
      </p>
    </div>
  );
}
